import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Divider } from 'antd';
import { ExternalLink } from 'lucide-react';

/**
 * NOTA: A altura do header é medida dinamicamente e exposta via CSS var
 * `--portal-header-height`. Evitar offsets "hardcoded" para não haver sobreposição.
 */
export const PORTAL_HEADER_CSS_VAR = '--portal-header-height';

/** Botões / triggers da barra principal (fundo verde). */
export function mainNavTriggerClass(isActive) {
  return [
    'flex shrink-0 items-center gap-1 px-3 py-2.5 text-sm font-semibold text-white transition md:px-5 md:text-base',
    isActive ? 'bg-black/20' : 'hover:bg-uem-primaryHover',
  ].join(' ');
}

/**
 * Casca visual inspirada no cabeçalho uem.mz (camadas 1–2 + nav principal opcional).
 */
export default function PortalUemHeader({
  onBrandClick,
  rightSlot,
  secondaryLinks = [],
  utilityLeft,
  utilityRight,
  mainNav,
  showSearchField = false,
  position = 'fixed',
  maxWidthClass = 'max-w-none',
}) {
  const headerRef = useRef(null);
  const positionClass =
    position === 'sticky'
      ? 'sticky top-0'
      : position === 'relative'
        ? 'relative'
        : 'fixed top-0 left-0 right-0';

  useEffect(() => {
    if (!headerRef.current) return;

    const el = headerRef.current;

    const writeHeight = () => {
      const height = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty(PORTAL_HEADER_CSS_VAR, `${Math.ceil(height)}px`);
    };

    writeHeight();

    const ro = new ResizeObserver(() => writeHeight());
    ro.observe(el);

    window.addEventListener('resize', writeHeight);
    return () => {
      window.removeEventListener('resize', writeHeight);
      ro.disconnect();
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className={`${positionClass} z-50 w-full ${maxWidthClass} bg-white shadow-md`}
    >
      {/* 1 — Utilitários */}
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-white bg-uem-utility">
        <div className="flex min-w-0 items-center gap-3">
          {utilityLeft}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {utilityRight !== undefined ? (
            utilityRight
          ) : (
            <a
              href="https://www.uem.mz"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-semibold uppercase tracking-wide hover:underline"
            >
              Universidade Eduardo Mondlane
              <ExternalLink size={14} className="opacity-90 text-white" />
            </a>
          )}
        </div>
      </div>

      {/* 2 — Marca + área direita */}
      <div
        className="flex flex-row flex-wrap items-center justify-between gap-3 border-b border-emerald-100/80 px-3 py-2 md:px-4"
        style={{
          background: 'linear-gradient(90deg, #ffffff 0%, #f0fdf4 45%, #ecfdf5 100%)',
        }}
      >
        <button
          type="button"
          onClick={onBrandClick}
          className="mb-0 flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent text-left"
        >
          <img
            src={require('../../assets/logo/logo-v2.png')}
            className="h-[76px] w-14 shrink-0 object-contain md:h-[88px] md:w-16"
            style={{ marginTop: 4 }}
            alt="UEM Logo"
          />
          <Divider
            type="vertical"
            className="hidden sm:block"
            style={{ height: '80px', borderWidth: '2px', borderColor: '#034a36' }}
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="text-2xl font-bold text-uem-primary md:text-3xl">Alumni</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-black md:text-sm">
              Portal do graduado
            </div>
          </div>
        </button>

        <div className="flex w-full shrink-0 flex-col items-stretch justify-end gap-2 sm:w-auto sm:flex-row sm:items-center">
          {showSearchField && (
            <a
              href="https://www.uem.mz"
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400 shadow-sm transition hover:border-uem-primary/40 hover:text-gray-600 sm:w-auto sm:min-w-[200px]"
            >
              <span aria-hidden>🔍</span>
              <span className="truncate">Procurar no site UEM…</span>
            </a>
          )}
          {rightSlot && <div className="flex shrink-0 items-center justify-end gap-2">{rightSlot}</div>}
        </div>
      </div>

      {/* 3 — Secundária (só se secondaryLinks tiver entradas) */}
      {secondaryLinks.length > 0 && (
        <nav
          className="flex items-center justify-center gap-0 overflow-x-auto border-b border-black/10 bg-uem-secondaryBar px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-white scrollbar-hide sm:text-xs"
          aria-label="Ligações rápidas"
        >
          {secondaryLinks.map((link, i) => (
            <React.Fragment key={link.href + link.label}>
              {i > 0 && <span className="mx-1 h-3 w-px shrink-0 bg-uem-separator sm:mx-2" />}
              {link.external ? (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 px-1.5 py-0.5 hover:text-emerald-300"
                >
                  {link.label}
                </a>
              ) : (
                <Link to={link.href} className="shrink-0 px-1.5 py-0.5 hover:text-emerald-300">
                  {link.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* 4 — Principal */}
      {mainNav != null && (
        <div className="border-t border-uem-separator bg-uem-primary text-white">
          <div
            className="flex min-h-[46px] w-full items-stretch overflow-x-auto scrollbar-hide"
          >
            <div className="mx-auto w-full max-w-[1400px]">
              <div className="flex w-full min-w-max items-stretch justify-between divide-x divide-white/25">
                {mainNav}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
